import {AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {OverviewField} from '../../core/definitions/object-view';
import {SearchObject} from '../../core/definitions/search-object';
import {MetaTypes} from '../../core/definitions/meta-types';

@Component({
    selector: 'app-search-columns-overview-fields',
    templateUrl: './search-columns-overview-fields.component.html',
    styleUrls: ['./search-columns-overview-fields.component.scss'],
    standalone: false
})
export class SearchColumnsOverviewFieldsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() searchObject: SearchObject;
  @Input() viewName: string;
  @Input() openLinksInTab: boolean;
  @Input() hideHeadline = false;
  @Input() constrainHeight = false;
  @Input() enableCollapsibleFields = true;
  @ViewChild('fieldContentElement') fieldContentElement?: ElementRef<HTMLDivElement>;

  MetaTypes = MetaTypes;
  visibleFields: Array<OverviewField> = [];
  readonly collapsedItemLimit = 5;
  expandedSmallContent = false;
  showMoreButtonVisible = false;

  private resizeObserver?: ResizeObserver;
  private readonly showMoreContainerBreakpoint = 595;

  constructor(private readonly ngZone: NgZone) {}

  ngOnChanges(): void {
    this.setVisibleFields();
    this.expandedSmallContent = false;
    this.refreshShowMoreVisibility();
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.refreshShowMoreVisibility();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get hasCollapsibleFields(): boolean {
    return this.enableCollapsibleFields && this.visibleFields.length > this.collapsedItemLimit;
  }

  toggleSmallContent(event: Event): void {
    event.stopPropagation();
    this.expandedSmallContent = !this.expandedSmallContent;
  }

  private getMaxVisibleFields(): number {
    return this.viewName === 'selector' ? 4 : 12;
  }

  private setVisibleFields() {
    if (!this.searchObject?.overview) {
      this.visibleFields = [];
      return;
    }

    this.visibleFields = this.searchObject.overview.slice(0, this.getMaxVisibleFields());
  }

  private setupResizeObserver(): void {
    if (!this.fieldContentElement?.nativeElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const width: number = entries[0]?.contentRect?.width ?? 0;

      this.ngZone.run(() => {
        this.refreshShowMoreVisibility(width);
      });
    });

    this.resizeObserver.observe(this.fieldContentElement.nativeElement);
  }

  private refreshShowMoreVisibility(widthOverride?: number): void {
    const width: number = widthOverride ?? this.fieldContentElement?.nativeElement?.getBoundingClientRect().width ?? 0;
    const isWithinBreakpoint: boolean = width > 0 && width <= this.showMoreContainerBreakpoint;

    this.showMoreButtonVisible = this.hasCollapsibleFields && isWithinBreakpoint;
  }
}
